import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  TrendingUp, TrendingDown, DollarSign, Car, MapPin, Clock, 
  Users, Star, BarChart3, RefreshCw, Loader2 
} from 'lucide-react';
import axios from 'axios';

/**
 * AnalyticsDashboard - Enhanced analytics for Admin Portal
 * Shows revenue trends, driver performance, popular routes, and more
 */
const AnalyticsDashboard = ({ backendUrl }) => {
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(30);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${backendUrl}/api/admin/analytics?days=${selectedDays}`);
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [selectedDays, backendUrl]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={fetchAnalytics}>Retry</Button>
      </Card>
    );
  }

  if (!analytics) return null;

  const { comparison, revenue_trend, top_drivers, vehicle_stats, popular_routes, hourly_distribution } = analytics;

  // Calculate max values for chart scaling
  const maxRevenue = Math.max(...(revenue_trend?.map(d => d.revenue) || [1]));
  const maxHourlyCount = Math.max(...(hourly_distribution?.map(d => d.count) || [1]));

  return (
    <div className="space-y-6">
      {/* Header with Time Filter */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <BarChart3 className="text-amber-500" size={24} />
          Analytics Dashboard
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={selectedDays}
            onChange={(e) => setSelectedDays(Number(e.target.value))}
            className="border rounded-lg px-3 py-2 text-sm"
            data-testid="analytics-days-filter"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Week-over-Week Comparison */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 border-2" data-testid="analytics-this-week-rides">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">This Week Rides</span>
            {comparison?.rides_change_percent >= 0 ? (
              <TrendingUp className="text-green-500" size={16} />
            ) : (
              <TrendingDown className="text-red-500" size={16} />
            )}
          </div>
          <p className="text-2xl font-bold">{comparison?.this_week_rides || 0}</p>
          <p className={`text-xs ${comparison?.rides_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {comparison?.rides_change_percent >= 0 ? '+' : ''}{comparison?.rides_change_percent || 0}% vs last week
          </p>
        </Card>

        <Card className="p-4 border-2" data-testid="analytics-this-week-revenue">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">This Week Revenue</span>
            {comparison?.revenue_change_percent >= 0 ? (
              <TrendingUp className="text-green-500" size={16} />
            ) : (
              <TrendingDown className="text-red-500" size={16} />
            )}
          </div>
          <p className="text-2xl font-bold">${comparison?.this_week_revenue || 0}</p>
          <p className={`text-xs ${comparison?.revenue_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {comparison?.revenue_change_percent >= 0 ? '+' : ''}{comparison?.revenue_change_percent || 0}% vs last week
          </p>
        </Card>

        <Card className="p-4 border-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Last Week Rides</span>
            <Car className="text-gray-400" size={16} />
          </div>
          <p className="text-2xl font-bold text-gray-600">{comparison?.last_week_rides || 0}</p>
        </Card>

        <Card className="p-4 border-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Last Week Revenue</span>
            <DollarSign className="text-gray-400" size={16} />
          </div>
          <p className="text-2xl font-bold text-gray-600">${comparison?.last_week_revenue || 0}</p>
        </Card>
      </div>

      {/* Revenue Trend Chart */}
      <Card className="p-6 border-2" data-testid="analytics-revenue-chart">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="text-green-500" size={18} />
          Revenue Trend
        </h3>
        {revenue_trend && revenue_trend.length > 0 ? (
          <div className="h-48">
            <div className="flex items-end h-40 gap-1 overflow-x-auto pb-2">
              {revenue_trend.map((day, index) => (
                <div key={index} className="flex flex-col items-center min-w-[40px]">
                  <div 
                    className="w-8 bg-gradient-to-t from-amber-500 to-yellow-400 rounded-t hover:from-amber-600 hover:to-yellow-500 transition-all cursor-pointer group relative"
                    style={{ height: `${Math.max((day.revenue / maxRevenue) * 100, 5)}%` }}
                    title={`$${day.revenue.toFixed(2)} - ${day.rides} rides`}
                  >
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      ${day.revenue.toFixed(2)} ({day.rides} rides)
                    </div>
                  </div>
                  <span className="text-[10px] text-gray-500 mt-1 rotate-45 origin-left">
                    {day._id?.split('-').slice(1).join('/')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No revenue data available</p>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Performing Drivers */}
        <Card className="p-6 border-2" data-testid="analytics-top-drivers">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Star className="text-amber-500" size={18} />
            Top Drivers
          </h3>
          {top_drivers && top_drivers.length > 0 ? (
            <div className="space-y-3">
              {top_drivers.slice(0, 5).map((driver, index) => (
                <div key={driver.id || index} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                  {driver.profile_photo ? (
                    <img 
                      src={driver.profile_photo.startsWith('http') ? driver.profile_photo : `${backendUrl}${driver.profile_photo}`}
                      alt={driver.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <Users size={16} className="text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{driver.name}</p>
                    <p className="text-xs text-gray-500">{driver.vehicle_type} • {driver.total_rides || 0} rides</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <Star size={12} className="fill-amber-400 text-amber-400" />
                      <span className="text-sm font-medium">{driver.rating?.toFixed(1) || 'N/A'}</span>
                    </div>
                    <p className="text-xs text-green-600">${driver.earnings?.toFixed(0) || 0}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No driver data available</p>
          )}
        </Card>

        {/* Rides by Vehicle Type */}
        <Card className="p-6 border-2" data-testid="analytics-vehicle-stats">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Car className="text-blue-500" size={18} />
            Rides by Vehicle Type
          </h3>
          {vehicle_stats && vehicle_stats.length > 0 ? (
            <div className="space-y-3">
              {vehicle_stats.map((stat, index) => (
                <div key={index} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium capitalize">{stat.vehicle_type}</span>
                    <span className="text-gray-600">{stat.total_rides} rides • ${stat.total_revenue}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full"
                      style={{ width: `${(stat.total_rides / Math.max(...vehicle_stats.map(v => v.total_rides))) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500">Avg fare: ${stat.avg_fare}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No vehicle data available</p>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Popular Routes */}
        <Card className="p-6 border-2" data-testid="analytics-popular-routes">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MapPin className="text-red-500" size={18} />
            Popular Routes
          </h3>
          {popular_routes && popular_routes.length > 0 ? (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {popular_routes.map((route, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" title={route.pickup}>
                        {route.pickup?.split(',').slice(0, 2).join(',')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 mt-1">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin size={10} className="text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate" title={route.dropoff}>
                        {route.dropoff?.split(',').slice(0, 2).join(',')}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>{route.count} trips</span>
                    <span>Avg ${route.avg_fare}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No route data available</p>
          )}
        </Card>

        {/* Hourly Distribution */}
        <Card className="p-6 border-2" data-testid="analytics-hourly-distribution">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Clock className="text-purple-500" size={18} />
            Peak Hours
          </h3>
          {hourly_distribution && hourly_distribution.length > 0 ? (
            <div className="h-48">
              <div className="flex items-end h-40 gap-[2px]">
                {Array.from({ length: 24 }, (_, hour) => {
                  const data = hourly_distribution.find(h => h.hour === hour);
                  const count = data?.count || 0;
                  const isPeak = count >= maxHourlyCount * 0.7;
                  return (
                    <div key={hour} className="flex flex-col items-center flex-1">
                      <div 
                        className={`w-full rounded-t transition-all cursor-pointer ${
                          isPeak 
                            ? 'bg-gradient-to-t from-purple-500 to-pink-400' 
                            : 'bg-gradient-to-t from-gray-300 to-gray-200'
                        }`}
                        style={{ height: `${Math.max((count / maxHourlyCount) * 100, 3)}%` }}
                        title={`${hour}:00 - ${count} rides`}
                      />
                      {hour % 4 === 0 && (
                        <span className="text-[9px] text-gray-500 mt-1">{hour}h</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                Purple bars indicate peak hours (70%+ of max)
              </p>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No hourly data available</p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
